import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type {
  ClaireLiveQualityReport,
  ClaireLiveQualityWindow,
} from '@borradh-workspace/api-client/types';
import {
  formatDay,
  formatShare,
  lastDays,
  targetStatus,
  weekChange,
} from './live-quality-format';
import type { ExplorerRequest } from './turn-explorer';

type Open = (r: ExplorerRequest) => void;
type Mistake = ClaireLiveQualityReport['types'][number]['label'];

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** A count that opens the conversations behind it. */
function CountButton({
  children,
  onClick,
  className,
}: {
  children: React.ReactNode;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'tabular-nums underline-offset-2 hover:underline',
        className
      )}
    >
      {children}
    </button>
  );
}

function SeriousTile({
  title,
  window,
  report,
  onOpen,
  footer,
}: {
  title: string;
  window: ClaireLiveQualityWindow | null;
  report: ClaireLiveQualityReport;
  onOpen: Open;
  footer?: React.ReactNode;
}) {
  const status = targetStatus(window?.seriousShare ?? null, report.target);
  return (
    <div className="rounded-xl border p-5">
      <div className="text-muted-foreground text-sm">{title}</div>
      <div className="flex items-center gap-3">
        <span
          className={cn(
            'text-4xl font-bold tabular-nums',
            status === 'above' && 'text-red-600 dark:text-red-400',
            status === 'met' && 'text-green-600 dark:text-green-400'
          )}
        >
          {formatShare(window?.seriousShare)}
        </span>
        {status === 'met' ? (
          <Badge variant="secondary">In target</Badge>
        ) : status === 'above' ? (
          <Badge variant="outline">Above target</Badge>
        ) : null}
      </div>
      {window && window.judged > 0 ? (
        <p className="text-muted-foreground mt-1 text-sm">
          <CountButton
            className="text-foreground font-medium"
            onClick={() =>
              onOpen({
                title: `Serious mistakes, ${title.toLowerCase()}`,
                filter: {
                  source: 'live',
                  from: window.from,
                  to: window.to,
                  mistake: 'serious',
                },
              })
            }
          >
            {window.serious}
          </CountButton>{' '}
          of {window.judged} replies had a serious mistake.
        </p>
      ) : (
        <p className="text-muted-foreground mt-1 text-sm">No judged replies.</p>
      )}
      {footer}
    </div>
  );
}

/** The serious-mistake share and what the mistakes are, day by day. */
export function MistakesSection({
  report,
  onOpen,
}: {
  report: ClaireLiveQualityReport;
  onOpen: Open;
}) {
  const days = lastDays(
    report.days.filter((d) => d.reliable),
    7
  );
  const change = weekChange(report.last7, report.previous7);
  const target = `${Math.round(report.target.low * 100)}–${Math.round(report.target.high * 100)}%`;
  const openType = (label: Mistake, from: string, to: string, when: string) =>
    onOpen({
      title: `${capitalise(label)}, ${when}`,
      filter: { source: 'live', from, to, mistake: label },
    });
  return (
    <section className="flex flex-col gap-4">
      <div>
        <h2 className="text-2xl font-semibold">Serious mistakes</h2>
        <p className="text-muted-foreground text-sm">
          A wrong claim, a request Claire did not act on, or a promise that the
          team would follow up when nobody was told. Goal: {target} of replies.
        </p>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <SeriousTile
          title={
            report.yesterday
              ? `Yesterday, ${formatDay(report.yesterday.from)}`
              : 'Yesterday'
          }
          window={report.yesterday}
          report={report}
          onOpen={onOpen}
        />
        <SeriousTile
          title={`Last 7 days, ${formatDay(report.last7.from)} – ${formatDay(report.last7.to)}`}
          window={report.last7}
          report={report}
          onOpen={onOpen}
          footer={
            change ? (
              <p
                className={cn(
                  'mt-1 text-sm font-medium',
                  change.better === true &&
                    'text-green-600 dark:text-green-400',
                  change.better === false && 'text-red-600 dark:text-red-400'
                )}
              >
                {change.text}
              </p>
            ) : null
          }
        />
      </div>
      <Card>
        <CardHeader>
          <CardTitle>What the mistakes are</CardTitle>
          <CardDescription>
            Replies with each kind of mistake. A reply can have more than one.
            Click a number to read those conversations.
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Mistake</TableHead>
                {days.map((d) => (
                  <TableHead key={d.day} className="text-right">
                    {formatDay(d.day)}
                  </TableHead>
                ))}
                <TableHead className="text-right">Last 7 days</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.types.map((t) => (
                <TableRow key={t.label}>
                  <TableCell>
                    <span className="flex items-center gap-2">
                      {capitalise(t.label)}
                      {t.serious ? (
                        <Badge variant="outline">Serious</Badge>
                      ) : null}
                    </span>
                  </TableCell>
                  {days.map((d) => (
                    <TableCell key={d.day} className="text-right">
                      <CountButton
                        onClick={() =>
                          openType(t.label, d.day, d.day, formatDay(d.day))
                        }
                      >
                        {d.byType[t.label]}
                      </CountButton>
                    </TableCell>
                  ))}
                  <TableCell className="text-right">
                    <CountButton
                      onClick={() =>
                        openType(
                          t.label,
                          report.last7.from,
                          report.last7.to,
                          'last 7 days'
                        )
                      }
                    >
                      {report.last7.byType[t.label]}
                    </CountButton>{' '}
                    <span className="text-muted-foreground">
                      (
                      {formatShare(
                        report.last7.judged
                          ? report.last7.byType[t.label] / report.last7.judged
                          : null
                      )}
                      )
                    </span>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell className="text-muted-foreground">
                  Replies judged
                </TableCell>
                {days.map((d) => (
                  <TableCell
                    key={d.day}
                    className="text-muted-foreground text-right tabular-nums"
                  >
                    {d.judged}
                  </TableCell>
                ))}
                <TableCell className="text-muted-foreground text-right tabular-nums">
                  {report.last7.judged}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </section>
  );
}
